import { useMutation, useQuery } from "@apollo/client";
import { InformationCircleIcon } from "@heroicons/react/outline";
import { ExclamationIcon, XIcon } from "@heroicons/react/solid";
import slackLogo from "@static/img/slack.png";
import { format } from "date-fns";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { OrgSettingsOrganizationFragmentFragment } from "types/graphql-schema";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Checkbox from "@components/checkbox";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import InputDatePicker from "@components/input/input-datepicker";
import AppLink from "@components/link/link";
import Tabs from "@components/tabs/tabs";
import { assertEdgesNonNullWithStringId, getAssetUrl } from "@helpers/helpers";

import backfillSlackDataMutation from "../graphql/backfill-slack-data";
import getSlackConfiguration from "../graphql/get-slack-configuration";
import removeSlackIntegrationMutation from "../graphql/remove-slack-integration-mutation";
import updateSlackOrgSettingsMutation from "../graphql/update-slack-org-settings";
import Notifications from "./notifications";
import SlackPermissionsDialog, {
  type SlackPermissions,
} from "./slack-permissions-dialog";

type SlackTab = "preferences" | "notifications" | "faq";

type SlackConfigurationProps = {
  organization: OrgSettingsOrganizationFragmentFragment & {
    publicSlackChannels?: any;
    slackIndexingConfiguration?: any;
  };
};

const SlackConfiguration = ({ organization }: SlackConfigurationProps) => {
  const { data } = useQuery(getSlackConfiguration, {
    variables: { organizationId: organization.id },
  });
  const [updateSlackSettings] = useMutation(updateSlackOrgSettingsMutation, {
    refetchQueries: [getSlackConfiguration],
  });
  const [runBackfill, { loading: backfillLoading }] = useMutation(
    backfillSlackDataMutation,
    {
      refetchQueries: [getSlackConfiguration],
    },
  );
  const [removeSlackIntegration, { loading: disconnecting }] = useMutation(
    removeSlackIntegrationMutation,
    {
      refetchQueries: [getSlackConfiguration],
    },
  );

  const [backfillDate, setBackfillDate] = useState<Date | null>(null);
  const [enableBackfill, setEnableBackfill] = useState(true);
  const [excludeNewChannels, setExcludeNewChannels] = useState(false);
  const [selectedTab, setSelectedTab] = useState<SlackTab>("preferences");
  const [showSlackPermissionsDialog, setShowSlackPermissionsDialog] =
    useState(false);
  const [slackPermissions, setSlackPermissions] = useState<SlackPermissions>({
    notifications: false,
    bot: false,
    ingest: false,
  });
  const slackConnectUrl = useMemo(
    () =>
      `/sso/slack?next=/settings/organization/${organization.id}/slack&org=${organization.id}`,
    [organization.id],
  );
  const tabOptions = useMemo<{ value: SlackTab; label: string }[]>(
    () => [
      { value: "notifications", label: "Notifications" },
      { value: "preferences", label: "Data Ingestion" },
      { value: "faq", label: "FAQ" },
    ],
    [],
  );

  const earliestBackfillDate = useMemo(() => {
    const channels =
      data?.organization?.slackIndexingConfiguration?.channels || [];
    const timestamps = channels
      .map((channel: any) => channel.firstIndexedTs)
      .filter(Boolean)
      .map((ts: string) => new Date(ts))
      .filter((date: Date) => !Number.isNaN(date.getTime()));
    if (!timestamps.length) {
      return null;
    }
    return new Date(
      Math.min.apply(
        null,
        timestamps.map((date: Date) => date.getTime()),
      ),
    );
  }, [data?.organization?.slackIndexingConfiguration?.channels]);

  const earliestFirstIndexedDate = useMemo(() => {
    const channels =
      data?.organization?.slackIndexingConfiguration?.channels || [];
    const timestamps = channels
      .map((channel: any) => channel.firstIndexedTs)
      .filter(Boolean)
      .map((ts: string) => new Date(ts))
      .filter((date: Date) => !Number.isNaN(date.getTime()));
    if (!timestamps.length) {
      return null;
    }
    return new Date(
      Math.min.apply(
        null,
        timestamps.map((date: Date) => date.getTime()),
      ),
    );
  }, [data?.organization?.slackIndexingConfiguration?.channels]);

  const backfillDescription = earliestBackfillDate
    ? `Messages from ${format(
        earliestBackfillDate,
        "MMMM d yyyy",
      )} and onward have been saved.`
    : "Choose a start date to backfill old data.";
  const hasDataIngestionPermissions =
    data?.organization?.slackIndexingConfiguration
      ?.hasDataIngestionPermissions ?? true;
  const hasNotificationPermissions =
    data?.organization?.slackIndexingConfiguration
      ?.hasNotificationPermissions ?? true;
  const hasBotPermissions =
    data?.organization?.slackIndexingConfiguration?.hasBotPermissions ?? false;

  const openPermissionsDialog = () => {
    setSlackPermissions({
      notifications: hasNotificationPermissions,
      bot: hasBotPermissions,
      ingest: hasDataIngestionPermissions,
    });
    setShowSlackPermissionsDialog(true);
  };

  const excludedFromConfig = useMemo(
    (): {
      id?: string | null;
      name?: string | null;
      shouldIndex?: boolean | null;
    }[] =>
      (data?.organization?.slackIndexingConfiguration?.channels || []).filter(
        (channel: any) => channel.shouldIndex === false,
      ),
    [data?.organization?.slackIndexingConfiguration?.channels],
  );

  const placeholderOption = useMemo(
    () => ({
      label: "Select a public channel to exclude",
      value: null,
      placeholder: true,
    }),
    [],
  );
  const [excludedChannel, setExcludedChannel] = useState(placeholderOption);

  const channelOptions = useMemo(() => {
    const connection =
      data?.organization?.publicSlackChannels ||
      organization.publicSlackChannels;
    if (!connection) {
      return [placeholderOption];
    }
    const excludedIds = new Set(
      excludedFromConfig
        .map((channel) => channel.id)
        .filter(Boolean) as string[],
    );
    const channels = assertEdgesNonNullWithStringId(connection)
      .filter((channel: any) => !excludedIds.has(channel.id))
      .map((channel: any) => ({
        label: `#${channel.name}`,
        value: channel.id,
        ...channel,
      }));
    return [placeholderOption, ...channels];
  }, [
    data?.organization?.publicSlackChannels,
    organization.publicSlackChannels,
    placeholderOption,
    excludedFromConfig,
  ]);

  useEffect(() => {
    if (
      data?.organization?.slackIndexingConfiguration
        ?.indexNewChannelsByDefault !== undefined
    ) {
      setExcludeNewChannels(
        !data.organization.slackIndexingConfiguration.indexNewChannelsByDefault,
      );
    }
  }, [
    data?.organization?.slackIndexingConfiguration?.indexNewChannelsByDefault,
  ]);

  useEffect(() => {
    if (earliestFirstIndexedDate && backfillDate === null) {
      setBackfillDate(earliestFirstIndexedDate);
    }
  }, [earliestFirstIndexedDate, backfillDate]);

  const handleToggleExcludeNewChannels = (checked: boolean) => {
    setExcludeNewChannels(checked);
    updateSlackSettings({
      variables: {
        organizationId: organization.id,
        indexNewChannelsByDefault: !checked,
      },
    });
  };

  const handleTabChange = (value: SlackTab) => {
    setSelectedTab(value);
    if (value === "notifications") {
      const el = document.getElementById("notifications-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <div className="space-y-8 text-gray-800">
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <AppLink
          to={`/settings/organization/${organization.id}/integrations`}
          className="hover:text-gray-800"
        >
          Organization Settings
        </AppLink>
        <span aria-hidden="true">›</span>
        <span className="text-gray-800 font-medium">Slack Configuration</span>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-4 items-start flex-1 min-w-0">
          <div className="border rounded-lg p-3 bg-white">
            <img
              src={getAssetUrl(slackLogo)}
              alt="Slack"
              className="h-12 w-12"
            />
          </div>
          <div className="space-y-2 flex-1 min-w-0">
            <div>
              <h1 className="text-3xl font-semibold">Slack Configuration</h1>
              <p className="text-gray-500 mt-2">
                Need help?{" "}
                <button
                  type="button"
                  onClick={() => window.Intercom?.("show")}
                  className="text-blue-600 hover:underline"
                >
                  Send us a message
                </button>{" "}
                on Intercom.
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            theme={buttonTheme.textRed}
            size={ButtonSize.medium}
            className="rounded-full px-4"
            disabled={disconnecting}
            onClick={() =>
              removeSlackIntegration({
                variables: { organizationId: organization.id },
              })
                .then(() =>
                  successNotificationVar({ title: "Slack disconnected" }),
                )
                .catch((error) =>
                  errorNotificationVar({
                    title: "Failed to disconnect Slack",
                    description: error?.message ?? "Please try again.",
                  }),
                )
            }
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </Button>
          <Button
            theme={buttonTheme.lightGray}
            size={ButtonSize.medium}
            className="rounded-full px-4"
            onClick={openPermissionsDialog}
          >
            Update Permissions
          </Button>
        </div>
      </div>

      <Tabs
        options={tabOptions}
        selectedValue={selectedTab}
        onClickOptionValue={handleTabChange}
        className="pt-4 w-full"
      />

      <div className="divide-y divide-gray-200">
        {selectedTab === "notifications" ? (
          <div id="notifications-section">
            {hasNotificationPermissions ? (
              <Notifications organization={organization} showLogo={false} />
            ) : (
              <div className="py-8">
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-6 flex flex-col items-center text-center gap-4">
                  <div className="bg-gray-100 rounded-md size-6 flex items-center justify-center">
                    <InformationCircleIcon className="size-4 text-gray-500" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-900">
                      Notifications are not connected
                    </p>
                    <p className="text-sm text-gray-800">
                      Update your Slack permissions to allow for Topicflow
                      notifications to be sent in Slack.
                    </p>
                  </div>
                  <Button
                    theme={buttonTheme.primary}
                    size={ButtonSize.medium}
                    className="w-fit"
                    onClick={openPermissionsDialog}
                  >
                    Update Permissions
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : selectedTab === "faq" ? (
          <SlackFaq />
        ) : !hasDataIngestionPermissions ? (
          <div className="py-8">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-6 flex flex-col items-center text-center gap-4">
              <div className="bg-gray-100 rounded-md size-6 flex items-center justify-center">
                <InformationCircleIcon className="size-4 text-gray-500" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">
                  Data ingestion is not connected
                </p>
                <p className="text-sm text-gray-800">
                  Update your Slack permissions to allow for data ingestion in
                  Topicflow.
                </p>
              </div>
              <Button
                theme={buttonTheme.primary}
                size={ButtonSize.medium}
                className="w-fit"
                onClick={openPermissionsDialog}
              >
                Update Permissions
              </Button>
            </div>
          </div>
        ) : (
          <>
            <SlackConfigurationRow
              title="Excluded Channels"
              description="Topicflow won’t read data from these channels."
            >
              <div className="max-w-md space-y-3">
                {excludedFromConfig.map((channel) => (
                  <div
                    key={channel.id}
                    className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 text-gray-700 bg-gray-50"
                  >
                    <span>#{channel.name}</span>
                    <button
                      type="button"
                      onClick={() =>
                        updateSlackSettings({
                          variables: {
                            organizationId: organization.id,
                            channelConfigurations: [
                              { channelId: channel.id, shouldIndex: true },
                            ],
                          },
                        })
                      }
                      className="text-gray-400 hover:text-gray-600"
                      aria-label={`Remove #${channel.name} from exclusions`}
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <ComboboxGeneric
                  containerClassName="w-full"
                  options={channelOptions}
                  value={excludedChannel as any}
                  onChangeValue={(option) => {
                    setExcludedChannel(option as any);
                    if (option?.value) {
                      updateSlackSettings({
                        variables: {
                          organizationId: organization.id,
                          channelConfigurations: [
                            { channelId: option.value, shouldIndex: false },
                          ],
                        },
                      });
                      setExcludedChannel(placeholderOption);
                    }
                  }}
                  placeholder="Select a public channel to exclude"
                />
              </div>
            </SlackConfigurationRow>

            <SlackConfigurationRow
              title="New Channel Indexing"
              description="This allows Topicflow to pull data from public channels created after integrating with Slack."
            >
              <div className="space-y-3">
                <Checkbox
                  label="Exclude new Slack channels from Topicflow’s indexing"
                  checked={excludeNewChannels}
                  onChange={handleToggleExcludeNewChannels}
                />
                {excludeNewChannels && (
                  <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-100 px-3 py-2 text-sm text-gray-600">
                    <ExclamationIcon className="size-4 text-amber-700 mt-0.5" />
                    <p>
                      Excluding future slack channels will impact the data
                      Topicflow ingests, affecting most features that make
                      Topicflow valuable to your team.
                    </p>
                  </div>
                )}
              </div>
            </SlackConfigurationRow>

            <SlackConfigurationRow
              title="Backfilled Data"
              description={backfillDescription}
            >
              <div className="space-y-4">
                <Checkbox
                  label="Pull from previous Slack data"
                  checked={enableBackfill}
                  onChange={setEnableBackfill}
                />
                <div className="flex flex-col items-start gap-3">
                  <InputDatePicker
                    date={backfillDate}
                    onChangeDate={setBackfillDate}
                    placeholderText="Select a start date"
                    dateFormat="MMMM d yyyy"
                    preserveYearLabel
                    disabled={!enableBackfill}
                    buttonProps={{
                      theme: buttonTheme.lightGray,
                      size: ButtonSize.medium,
                      rounded: true,
                    }}
                  />
                  <Button
                    theme={buttonTheme.primary}
                    size={ButtonSize.medium}
                    className="w-fit"
                    disabled={
                      !enableBackfill || !backfillDate || backfillLoading
                    }
                    onClick={() => {
                      if (!enableBackfill || !backfillDate) {
                        return;
                      }
                      runBackfill({
                        variables: {
                          organizationId: organization.id,
                          startDate: backfillDate.toISOString(),
                        },
                      })
                        .then(() => {
                          successNotificationVar({
                            title: "Starting backfill",
                          });
                        })
                        .catch((error) => {
                          errorNotificationVar({
                            title: "Backfill failed",
                            description:
                              error?.message || "Please try again in a moment.",
                          });
                        });
                    }}
                  >
                    {backfillLoading ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            </SlackConfigurationRow>
          </>
        )}
      </div>
      <SlackPermissionsDialog
        open={showSlackPermissionsDialog}
        initialPermissions={slackPermissions}
        onClose={() => setShowSlackPermissionsDialog(false)}
        onContinue={(permissions) => {
          setSlackPermissions(permissions);
          setShowSlackPermissionsDialog(false);
          const perms = Object.entries(permissions)
            .filter(([, enabled]) => enabled)
            .map(([key]) => key)
            .join(",");
          const separator = slackConnectUrl.includes("?") ? "&" : "?";
          const url =
            perms.length > 0
              ? `${slackConnectUrl}${separator}perms=${encodeURIComponent(perms)}`
              : slackConnectUrl;
          window.location.href = url;
        }}
      />
    </div>
  );
};

const SlackConfigurationRow = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) => (
  <div className="py-8 grid gap-4 sm:grid-cols-[240px_minmax(0,1fr)]">
    <div>
      <p className="font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </div>
    <div>{children}</div>
  </div>
);

const SlackFaq = () => {
  const faqItems = [
    {
      question: "What does the Slack integration do?",
      answer: (
        <p className="text-gray-600">
          Topicflow uses this integration to pull in messages from approved
          Slack channels. These messages help provide richer context across
          Topicflow—powering things like AI-generated insights, summaries, and
          performance-related content.
        </p>
      ),
    },
    {
      question: "Does Topicflow access DMs or private conversations?",
      answer: (
        <div className="text-gray-600 space-y-2">
          <p>No. Topicflow does not access:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Direct messages (DMs)</li>
            <li>Group DMs</li>
            <li>Private channels</li>
          </ul>
          <p>
            Only public channels are included by default, and admins can manage
            what channels are indexed.
          </p>
        </div>
      ),
    },
    {
      question: "Who can set up the Slack integration?",
      answer: (
        <p className="text-gray-600">
          Only a Slack admin can install Topicflow’s Slack integration. This
          ensures that the necessary permissions are granted for
          organization-wide access.
        </p>
      ),
    },
    {
      question: "What channels are included after setup?",
      answer: (
        <div className="text-gray-600 space-y-2">
          <p>By default, all public channels will be indexed.</p>
          <p>Admins can:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Exclude any specific public channels</li>
            <li>
              Choose whether or not new channels added in the future should be
              indexed
            </li>
            <li>Manage these settings at any time in Topicflow</li>
          </ul>
        </div>
      ),
    },
    {
      question: "Can we backfill historical messages from Slack?",
      answer: (
        <div className="text-gray-600 space-y-2">
          <p>Yes. During setup, admins can choose to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Enable historical backfill</li>
            <li>
              Select how far back in time to index (e.g. 30 days, 90 days)
            </li>
          </ul>
          <p>
            Backfill happens asynchronously in the background after
            installation.
          </p>
        </div>
      ),
    },
    {
      question: "What happens if a Slack message is edited or deleted?",
      answer: (
        <div className="text-gray-600 space-y-2">
          <p>If a message is edited or deleted after it has been indexed:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Topicflow will update or remove the message from its system
              accordingly
            </li>
            <li>
              This helps ensure AI-generated content reflects the latest Slack
              data
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "How does Topicflow ensure privacy and security?",
      answer: (
        <div className="text-gray-600 space-y-2">
          <ul className="list-disc pl-6 space-y-1">
            <li>We never ingest DMs, private channels, or group chats</li>
            <li>Admins have full control over which channels are included</li>
            <li>Slack user IDs are stored in a privacy-conscious way</li>
          </ul>
        </div>
      ),
    },
    {
      question: "Can we remove a channel’s data after it’s been indexed?",
      answer: (
        <p className="text-gray-600">
          Yes. Admins can exclude a channel at any time to stop further indexing
          and remove previously indexed data.
        </p>
      ),
    },
  ];

  return (
    <div className="py-8 space-y-8">
      {faqItems.map(({ question, answer }, index) => (
        <div key={question} className="space-y-2">
          <p className="font-semibold text-gray-900">{`${index + 1}. ${question}`}</p>
          {answer}
        </div>
      ))}
    </div>
  );
};

export default SlackConfiguration;
